import {
	CellRange, CellSource, ColumnHeaderModel, ColumnSource, CommentSource, ConditionalFormatItemSource, ConditionalFormatSource,
	Grid, GridAdapter, GridColumnResizeEvent, Options, Renderer, RequestFilters, RowSource, renderers, CellModel,
	Style,
	GridSource,
} from "@omegagrid/grid";
import { utils } from '@omegagrid/core';
import { AxisIndexItem, AxisResult, Index, IndicatorResult } from "../../types";
import { ReportResult } from "../../model/reportResult";
import { IndicatorRenderer, ReportContext, ContextData, TableViewColumnsOptions, TableViewOptions } from "@reporting/rp-types/analytics";
import { createCellRenderer } from "./renderers";
import { convertLegacyCellStyles } from "./tableLegacyStyle";
import { msg } from "@omegagrid/localize";
import rp from "@reporting/rp-app-host";

const DEFAULT_COLUMN_WIDTH = 120;
const DEFAULT_FIRST_COLUMN_WIDTH = 300;
const TOTAL_BORDER_COLOR = '888888';
const WATERFALL_ROW_HEIGHT = 200;
const WATERFALL_HEADER_WIDTH = 40;
const WATERFALL_HEADER_HEIGHT = 100;
const WATERFALL_HEADER_STYLE: Style = {
	alignment: {
		vertical: 'bottom',
		horizontal: 'center',
		textRotation: 90
	}
};

type ValueColumnSource = ColumnSource & {
	_index?: AxisIndexItem,
	_indicator?: IndicatorResult
};

export type AxisItemProps = {
	indicator?: IndicatorResult,
	renderer?: IndicatorRenderer,
	items?: {id: string, dim: string, dimNum: string}[],
	/** stable business key of the axis item (dim item ids + indicator), pairs comments with cells */
	key?: string,
};

type DimItems = { id: string, dim: string, dimNum: string };

function normalizeLevels<TRow extends {l?: number}>(nodes: TRow[]) {
	let currentLevel = -1;
	const levelMap: Record<number, number> = {};

	for (const node of nodes) {
		if (levelMap[node.l] != null) {
			currentLevel = levelMap[node.l];
			node.l = currentLevel;
		} else if (node.l > currentLevel) {
			for (let l = 0; l < node.l; l++) {
				if (levelMap[l] == null) {
					levelMap[l] = l;
				}
			}
			levelMap[node.l] = ++currentLevel;
			node.l = currentLevel;
		} else {
			node.l = currentLevel;
		}
	}

	return nodes;
}

function filterLowerLevels<TRow extends {l?: number, _a?: number}>(items: TRow[]): TRow[] {
	if (items.length === 0) return [];

	const result: TRow[] = [];
	let islandStart = 0;

	for (let i = 0; i <= items.length; i++) {
		// End of island (change of l or end of array)
		if (i === items.length || items[i].l !== items[islandStart].l) {
			// Find minimum a for current island
			const island = items.slice(islandStart, i);
			const minA = Math.min(...island.map(item => item._a));

			// Add only items with minimum a
			result.push(...island.filter(item => item._a === minA));
			islandStart = i;
		}
	}

	return result;
}

function formatBlankRow(row: RowSource, textColumnOffset: number) {
	row.data = [];
	row.p = null;
	row.clickable = false;
	row.s = {
		left: { color: rp.theme.activeTheme.definition['cell-background'].substring(1) },
		right: { color: rp.theme.activeTheme.definition['cell-background'].substring(1) },
	};
	row.renderer = (div: HTMLDivElement, cell: CellModel) => {
		div = renderers.BaseRenderer(div, cell);
		div.innerHTML = '';
		return div;
	};
	return row;
}

export function getGridSource(result: ReportResult, reportContext: ReportContext) {
	const adapter = new TableAdapter(result, reportContext);
	adapter.hasLevelColumn = false;
	const sourceData: GridSource = {
		rows: adapter.getRows(),
		columns: adapter.getColumns(),
		options: {rowNumbers: true, colLetters :true, autofill: false, columnTree: false}
	};

	// move headers to the top of the rows
	let headers = 0;
	sourceData.columns.forEach(col => {
		headers = Math.max(headers, col.header?.length || 0);
	});

	const headerRows: RowSource[] = (new Array(headers)).fill(null).map(() => ({
		data: [] as any, 
		s: {bold: true, alignment: {horizontal: 'center'}}
	}));
	sourceData.columns.forEach(col => {
		if (!col.header) return;
		const header = col.header as ColumnHeaderModel[];
		col.header = null;
		header.forEach((h, rowIndex) => headerRows[rowIndex].data.push({v: h.label, t: 't'}));
	});

	sourceData.rows = [...headerRows, ...sourceData.rows];
	//////////////////////////////////////

	return sourceData;
}

const KEY_SEPARATOR = '-';

function escapeKeySegment(segment: string) {
	return segment.replace(new RegExp(KEY_SEPARATOR, 'g'), KEY_SEPARATOR + KEY_SEPARATOR);
}

function createKey(dimItems: DimItems[], indicator?: IndicatorResult) {
	return dimItems.map(item => escapeKeySegment(item.id)).join(KEY_SEPARATOR)
		+ (indicator ? (dimItems.length > 0 ? KEY_SEPARATOR : '') + escapeKeySegment(indicator.key) : '');
}

export class TableAdapter extends GridAdapter {

	private _textColumns: ColumnSource[];
	private _valueColumns: ValueColumnSource[];
	private _columns: ValueColumnSource[];
	private _rows: RowSource[];
	private _groupRows: Set<number>;
	private _rowStyles: Map<number, string>;
	private _customRendererColumns: Set<number>;
	private _customRendererRows: Set<number>;
	private _hasLevelColumn: boolean;

	/**
	 * An active table-wide theme replaces the legacy per-row styles. The server
	 * already strips them from fresh data when the context has a theme; this
	 * flag (kept in sync by the visualization) covers results that were loaded
	 * into the browser before the theme was picked.
	 */
	public suppressLegacyStyles = false;

	constructor(
		readonly result: ReportResult,
		readonly reportContext: ReportContext
	) { super() }

	public get hasLevelColumn() { 
		if (this._hasLevelColumn != null) return this._hasLevelColumn;
		this._hasLevelColumn = this.reportContext.context.module == 'F' && (this.reportContext.context as ContextData).y_axis[0]?.dim == 'R';
		return this._hasLevelColumn;
	}

	public set hasLevelColumn(value: boolean) {
		this._hasLevelColumn = value;
	}

	private get textColumnOffset() { return this.hasLevelColumn ? 1 : 0 }
	private get valueColumnOffset() { return this.getTextColumns().length }
	private get headerRowCount() { return this.result.xAxis.length; }
	private get contextData() { return this.reportContext.context as ContextData }


	private _cellRenderers = new Map<IndicatorRenderer, Renderer>();
	private getCellRenderer(type: IndicatorRenderer, direction: 'row' | 'column'): Renderer {
		if (this._cellRenderers.has(type)) return this._cellRenderers.get(type);
		const renderer = createCellRenderer(type, {
			context: this.reportContext,
			reportResult: this.result,
			scope: direction,
			direction: direction,
			firstIndex: direction == 'column' ? 0 : this._textColumns?.length ?? 0
		});
		this._cellRenderers.set(type, renderer);
		return renderer;
	}

	protected async attached(grid: Grid): Promise<void> {
		this.getColumns();
		if (this._customRendererColumns.size === 0) return;
		grid.addEventListener('grid.column.resize', (e: GridColumnResizeEvent) => {
			if (!this._customRendererColumns.has(e.column.index)) return;
			const cells = e.grid.model.getCellArray(CellRange.fromA1(e.column.address));
			e.grid.rendering.updateCells(cells);
		});
	}

	private getAxisGroups(axis: AxisResult[]) {
		if (!axis || axis.length === 0) return [];
		
		const axisGroups: AxisResult[][] = [[]];
		axis[0].break = false;
		for (const axisItem of axis) {
			if (axisItem.dimension == 'UM') continue;
			if (axisItem.break) axisGroups.push([]);
			axisGroups[axisGroups.length - 1].push(axisItem);
		}
		return axisGroups;
	}

	private getTextColumns(): ColumnSource[] {
		if (this._textColumns) return this._textColumns;

		this._textColumns = [];
		let dataIndex = 0;

		if (this.hasLevelColumn) {
			this._textColumns.push({
				w: 45,
				t: 'n',
				z: '(0)',
				s: {alignment: {horizontal: 'center'}},
				data: dataIndex++,
				levelIndent: false,
				clickable: false,
				levelButtons: true,
				priority: 3,
				name: 'C_LEVEL'
			});
		}

		if (this.result?.yAxis.length) {
			const headerCount = this.result.headerCount;
			const hasIndentColumn = this.result.yAxis[0].columnIndex?.indent > -1;
			const axisGroups = this.getAxisGroups(this.result.yAxis);

			let index = 0;
			for (const axisGroup of axisGroups) {
				const headerText = [...new Set(axisGroup.map(a => msg(a.description)))].join(' | ');
				const header: ColumnHeaderModel[] = (new Array(headerCount)).fill(null).map((_, i) => (i == headerCount - 1 ? {label: headerText} : {}));
			
				this._textColumns.push({
					w: axisGroups.length == 1 ? DEFAULT_FIRST_COLUMN_WIDTH : DEFAULT_COLUMN_WIDTH,
					t: 't',
					data: dataIndex + index,
					levelIndent: !hasIndentColumn && index == 0,
					levelButtons: !this.hasLevelColumn && index == 0,
					clickable: true,
					sorting: true,
					header: header,
					priority: 4,
					name: `C_DIM_${axisGroup.map(g => g.dimension ?? 'X')}`,
					props: {key: `DIM_${axisGroup.map(g => g.dimension ?? 'X')}`}
				})

				++index;
			}
		}
		
		return this._textColumns;
	}

	private getValueColumns() {
		if (this._valueColumns) return this._valueColumns;

		this._valueColumns = [];
		this._customRendererColumns = new Set<number>();

		const textColumnCount = this.getTextColumns().length;
		const axisLevelCount = this.result.xAxis.length;
		const indicatorAxis = this.result.indicatorAxis;
		const indicatorAxisLevel = indicatorAxis.axisIndex == 0 ? indicatorAxis.axisLevel : -1;
		const headers: [ColumnHeaderModel, string][][] = (new Array(axisLevelCount)).fill(null).map<[ColumnHeaderModel, string][]>(() => []);
		const levelOffsets = new Array(axisLevelCount).fill(0);
		let hasTotal = false;

		for (let i = 1; i < axisLevelCount; i++) {
			levelOffsets[i] = levelOffsets[i - 1] + this.result.xAxis[i - 1].levelCount;
		}

		// add single column on empty axis
		const xIndex: AxisIndexItem[] = this.result.xIndex.length ? this.result.xIndex : [
			[null, [0], 0, 0]
		];

		for (let i = 0; i < xIndex.length; i++) {
			const index = xIndex[i];
			const indicator = indicatorAxis.axisIndex == 0 ? this.result.indicators[index[1][indicatorAxisLevel]] : null;
			const column: ValueColumnSource = {w: DEFAULT_COLUMN_WIDTH, t: 'n'};
			const header: ColumnHeaderModel[] = [];
			const dimItems: {id: string, dim: string, dimNum: string}[] = [];
			const nameParts: string[] = [];
			let viewOptions: any;
			let isTotal = false;

			let level = 0;
			for (let axisLevel = 0; axisLevel < axisLevelCount; axisLevel++) {
				const axis = this.result.xAxis[axisLevel];
				const h: ColumnHeaderModel = {label: null};
				if (axis.axisType == 'dimension') {
					const rowIndex = index[1][axisLevel];
					const dataRow = axis.data[rowIndex];

					if (dataRow && dataRow[axis.columnIndex.idz] < -1) isTotal = true;

					if (dataRow) {
						const namePart = this.getAxisItemName(axis, dataRow);
						if (namePart != null) nameParts.push(namePart);

						dimItems.push({
							id: dataRow[axis.columnIndex.id],
							dim: this.getAxisDimLabel(axis, dataRow, indicator),
							dimNum: axis.dimension
						});

						if (axis.columnIndex.view_options > -1) {
							viewOptions = dataRow[axis.columnIndex.view_options];
						}
					}

					if (rowIndex == null) {
						h.label = '[subtotal]';
						headers[axisLevel].push([h, null]);
					} else {
						if (dataRow) {
							// indicator-specific label only below the indicator level, where the
							// header cell is not spanned across multiple indicators
							h.label = axisLevel > indicatorAxisLevel
								? this.getAxisDimLabel(axis, dataRow, indicator)
								: dataRow[axis.columnIndex.dim];
							level = levelOffsets[axisLevel] + parseInt(dataRow[axis.columnIndex.level]);
						}
						headers[axisLevel].push([h, rowIndex.toString()]);
					}
				} else if (axis.axisType == 'indicators' && indicator) {
					if (indicator.dataColumn) nameParts.push(indicator.dataColumn);
					h.label = indicator.label;
					headers[axisLevel].push([h, indicator.dataColumn]);
				} else {
					h.label = '[???]';
					headers[axisLevel].push([h, null]);
				}

				header.push(h);
			}

			column._index = index;
			column._indicator = indicator;
			// name for the theme name matchers, e.g. C_2027_TO_1_A9153C4A
			if (nameParts.length) column.name = 'C_' + nameParts.join('_');
			column.header = header;
			column.l = level;
			column.sorting = true;
			column.data = this._valueColumns.length + textColumnCount;
			column.clickable = true;
			column.commentable = true;
			column.props = {
				indicator: indicator,
				items: dimItems,
				renderer: indicator?.renderer as IndicatorRenderer,
				viewOptions: viewOptions,
				key: createKey(dimItems, indicator),
			} as AxisItemProps;

			if (indicator) {
				column.renderer = indicator.renderer ? this.getCellRenderer(indicator.renderer as IndicatorRenderer, 'column') : null;

				if (column.renderer) {
					this._customRendererColumns.add(this._textColumns.length + this._valueColumns.length);
				}

				if (indicator.format != null && indicator.format != '') {
					column.z = indicator.format;
					column.priority = 3;
				}
			}

			if (isTotal) {
				if (!hasTotal) {
					hasTotal = true;
					column.s = {left: {style: 'double', color: TOTAL_BORDER_COLOR}};
				}
				column.clickable = false;
			}

			this._valueColumns.push(column);
		}

		// update column spans
		for (let axisLevel = 0; axisLevel < axisLevelCount - 1; axisLevel++) {
			let currentHeader: ColumnHeaderModel;
			let currentKey: string;
			for (let i = 0; i < headers[axisLevel].length; i++) {
				const [header, key] = headers[axisLevel][i];
				if (currentKey !== header.label) {
					currentHeader = header;
					currentKey = header.label;
					currentHeader.cs = 1;
				} else {
					currentHeader.cs++;
				}
			}
		}

		return this._valueColumns;
	}

	getColumns(): ColumnSource[] {
		const columnOptions = (this.contextData.view_options as TableViewOptions)?.columns || {};

		// re-apply the options even on the cached source - the user may have
		// resized columns since the source was built and a refresh (e.g. the
		// theme preview) would reset the widths to the stale cached ones
		if (this._columns) return this.applyColumnOptions(this._columns, columnOptions);

		const textColumns = this.getTextColumns();
		const valueColumns = this.getValueColumns();

		if (textColumns.length > 0) {
			textColumns[0].header ??= [{}];
			(textColumns[0].header[0] as ColumnHeaderModel).cs = textColumns.length;
		}

		this._columns = this.applyColumnOptions([
			...textColumns,
			...valueColumns
		], columnOptions);

		return this._columns;
	}

	private applyColumnOptions(columns: ColumnSource[], options: TableViewColumnsOptions) {
		for (let c = 0; c < columns.length; c++) {
			if (options[c]) {
				if (options[c].w != null) columns[c].w = options[c].w;
			}
		}
		return columns;
	}

	/**
	 * Name part of an axis item used for the theme name matchers: the finance
	 * statement rows carry their row key in KEY (ID_DIM holds ID_ROW there),
	 * every other dimension uses ID_DIM.
	 */
	private getAxisItemName(axis: AxisResult, dataRow: any[]): string|null {
		const key = axis.columnIndex.key > -1 ? dataRow[axis.columnIndex.key] : null;
		const name = key ?? (dataRow[axis.columnIndex.id] + '_' + dataRow[axis.columnIndex.dim]);
		return name == null || name === '' ? null : String(name);
	}

	/** Row name for the theme name matchers, e.g. R_trzby or R_100_TO_1 */
	private composeRowName(yAxis: AxisResult[], itemIndexes: Index[], indicatorAxisLevel: number, count?: number): string|null {
		const parts: string[] = [];
		const n = count ?? itemIndexes.length;
		for (let j = 0; j < n && j < yAxis.length; j++) {
			const index = itemIndexes[j];
			if (index == null || index < 0) continue;
			if (yAxis[j].axisLevel == indicatorAxisLevel && yAxis[j].axisType == 'indicators') {
				const dataColumn = this.result.indicators[index]?.dataColumn;
				if (dataColumn) parts.push(dataColumn);
			} else {
				const dataRow = yAxis[j].data?.[index];
				const namePart = dataRow ? this.getAxisItemName(yAxis[j], dataRow) : null;
				if (namePart != null) parts.push(namePart);
			}
		}
		return parts.length ? 'R_' + parts.join('_') : null;
	}

	private getAxisItemDimProps(axis: AxisResult, index: number) {
		const dataRow = axis.data[index];
		if (!dataRow) return null;
		return {
			id: dataRow[axis.columnIndex.id],
			dim: dataRow[axis.columnIndex.dim],
			dimNum: axis.dimension
		};
	}

	/**
	 * Dimension label of an axis item; for a period axis with relative-period (PR)
	 * indicators the INDICATOR_DIM column overrides the base period name with the
	 * name of the period the indicator actually shows.
	 */
	private getAxisDimLabel(axis: AxisResult, dataRow: any[], indicator: IndicatorResult | null): string {
		const indicatorDimIndex = axis.columnIndex.indicator_dim;
		if (indicator && indicatorDimIndex > -1) {
			const indicatorDims = dataRow[indicatorDimIndex];
			const dim = Array.isArray(indicatorDims) ? indicatorDims[indicator.index] : null;
			if (dim != null) return dim;
		}
		return dataRow[axis.columnIndex.dim];
	}

	getRows(): RowSource[] {
		if (this.result.isEmpty) return [];
		if (this._rows) return this._rows;
		this._customRendererRows = new Set<number>();

		const rows: (RowSource & {_i?: IndicatorResult, _g?: boolean, _a?: number})[] = [];
		this._groupRows = new Set();
		this._rowStyles = new Map();
		const tableType = this.contextData.view  == 'table3' ? 3 : this.contextData.view == 'table2' ? 2 : 1;
		const nullsToZeros = this.result.nullsToZeros;
		const valueColumns = this.getValueColumns();
		const textColumns = this.getTextColumns();
		const indicatorAxis = this.result.indicatorAxis;
		const indicators = this.result.indicators;
		const indicatorAxisIndex = indicatorAxis.axisIndex;
		const hasIndicators = indicatorAxisIndex == 1;
		const indicatorAxisLevel = hasIndicators ? indicatorAxis.axisLevel : -1;
		const yAxis = this.result.yAxis;
		const hasLevelColumn = this.hasLevelColumn;
		const textColumnOffset = this.textColumnOffset;
		const parentBasedTree = this.result.yParentBasesAxis > -1;
		const yAxisGroups = this.getAxisGroups(this.result.yAxis);
		const yAxisLevelGroupIndexMap = new Map<number, number>(
			yAxisGroups.flatMap((group, index) => group.map((axis) => [axis.axisLevel, index]))
		);

		let yKey: string, xKey: string;
		let yIndexItem: AxisIndexItem;
		let yAxisGroup: AxisResult[];
		let yAxisItem: AxisResult;
		let yAxisItemData: any[];
		let yAxisItemDataIndex: number;
		let yIndentIndex: number;
		let yHiddenIndex, yDrillIndex, yFormatIndex, yStyleIndex, yCollapsedIndex, yViewOptionsIndex, yIdzIndex: number;
		let dataRow: any[];
		let dataRowIndex: number;
		let value: any;
		let unit: string;
		let displayUnit: number;
		let valueColumn: ValueColumnSource;
		let indicator: IndicatorResult;
		let i: number, j: number, k: number, l: number;
		let itemLevel: number;
		let axisLevel: number;
		let row: RowSource & {_i?: IndicatorResult, _g?: boolean, _a?: number};
		let itemIndexes: Index[] = [];
		let prevItemIndexes: Index[] = [];
		let isIndicator: boolean;
		let indicatorIndex = -1;
		let dimItems: DimItems[];
		let lastDimValues: string[][];
		let lastIndent: number;
		let indent: number;
		let hasTotal = false;

		lastDimValues = tableType == 3 ? this.result.yAxis.map((axis) => new Array(axis.levelCount)) : null;

		// add single row on empty axis
		const yIndex: AxisIndexItem[] = this.result.yIndex.length ? this.result.yIndex : [
			[null, [0], 0, 0]
		];

		for (i = 0; i < yIndex.length; i++) {
			yIndexItem = yIndex[i];
			yKey = yIndexItem[0];
			itemIndexes = yIndexItem[1];
			axisLevel = yIndexItem[2];
			itemLevel = yIndexItem[3];
			yAxisItem = yAxis[axisLevel];
			yAxisItemDataIndex = yIndexItem[1][axisLevel];
			yAxisItemData = yAxisItem?.data?.[yAxisItemDataIndex];
			yIndentIndex = yAxisItem?.columnIndex?.indent;
			yHiddenIndex = yAxisItem?.columnIndex?.hidden;
			yDrillIndex = yAxisItem?.columnIndex?.drill;
			yFormatIndex = yAxisItem?.columnIndex?.format;
			yStyleIndex = yAxisItem?.columnIndex?.style;
			yCollapsedIndex = yAxisItem?.columnIndex?.collapsed;
			yViewOptionsIndex = yAxisItem?.columnIndex?.view_options;
			yIdzIndex = yAxisItem?.columnIndex?.idz;
			dimItems = [];

			// grouping rows with no values
			if (hasIndicators) {
				for (j = 0; j < yAxis.length; j++) {
					isIndicator = yAxis[j].axisLevel == indicatorAxisLevel;
					itemLevel += isIndicator ? 1 : 0;

					if (j < axisLevel && itemIndexes[j] !== prevItemIndexes[j]) {
						row = {data: [], l: parentBasedTree ? null : yIndexItem[3], props: {}, _g: true, _a: yIndexItem[2]};
						row.name = this.composeRowName(yAxis, itemIndexes, indicatorAxisLevel, j + 1);

						if (isIndicator) {
							row.data[textColumnOffset] = indicators[itemIndexes[j]].label;
						} else {
							row.data[textColumnOffset] = this.getAxisDimLabel(
								yAxis[j],
								yAxis[j].data[itemIndexes[j]],
								j > indicatorAxisLevel ? indicators[itemIndexes[indicatorAxisLevel]] : null
							);
						}

						rows.push(row);

						// row dimension description
						k = itemIndexes[j];
						if (yAxis[j] && k != null) {
							const props = this.getAxisItemDimProps(yAxis[j], k);
							if (props) dimItems.push(props);
						}
					}
				}
			} else {
				// row dimension description
				for (j = 0; j < itemIndexes.length; j++) {
					k = itemIndexes[j];
					if (yAxis[j] && k != null) {
						const props = this.getAxisItemDimProps(yAxis[j], k);
						if (props) dimItems.push(props);
					}
				}
			}

			if (hasIndicators) {
				indicatorIndex = yIndexItem[1][indicatorAxisLevel];
				indicator = indicators[indicatorIndex];
			} else {
				indicator = null;
			}

			row = { data: [] };

			if (hasLevelColumn) {
				row.data[0] = itemLevel;
			}

			// row tree structure based on parent indices or level numbers
			if (parentBasedTree) {
				row.p = yIndexItem[4];
			} else {
				row.l = itemLevel;
			}

			// text column values
			if (axisLevel == indicatorAxisLevel) {
				row.data[textColumnOffset] = indicator?.label;
				row._i = indicator;
			} else if (yAxisItem && yAxisItemDataIndex > -1) {
				for (j = 0; j < axisLevel; j++) {
					const subIndexItem = yAxis[j];
					row.data[textColumnOffset + j] = this.getAxisDimLabel(
						subIndexItem,
						subIndexItem.data[yIndexItem[1][j]],
						j > indicatorAxisLevel ? indicator : null
					);
				}
				row.data[textColumnOffset + yAxisLevelGroupIndexMap.get(axisLevel)] = this.getAxisDimLabel(
					yAxisItem,
					yAxisItemData,
					axisLevel > indicatorAxisLevel ? indicator : null
				);
			} else if (indicator) {
				row.data[textColumnOffset] = indicator.label;
				row._i = indicator;
			}

			// properties for group by and drill
			indent = yIndentIndex > -1 && yAxisItemDataIndex > -1 && yAxisItem ? parseInt(yAxisItemData[yIndentIndex]) : undefined;

			if (indent == null && lastIndent != null) {
				indent = lastIndent + (row.l ?? 0) + 1;
			} else {
				lastIndent = indent;
			}

			row.props = {
				indicator: hasIndicators ? indicator : undefined,
				items: dimItems,
				indent: indent,
				key: createKey(dimItems, hasIndicators ? indicator : undefined)
			} as AxisItemProps;

			row.name = this.composeRowName(yAxis, itemIndexes, indicatorAxisLevel);
			row._a = yIndexItem[2];

			if (!hasTotal && axisLevel == 0 && itemLevel == 0) {
				if (yAxisItemData && parseInt(yAxisItemData[yIdzIndex]) < -1) {
					row.s = {top: {style: 'double', color: TOTAL_BORDER_COLOR}};
					row.data = row.data.map((v: any) => ({v: v, clickable: false}));
					hasTotal = true;
				}
			}

			// row based display properties
			if (yAxisItem && yAxisItemDataIndex > -1) {
				if (yStyleIndex > -1 && yAxisItemData[yStyleIndex]) this._rowStyles.set(rows.length, yAxisItemData[yStyleIndex]);
				if (yFormatIndex > -1 && yAxisItemData[yFormatIndex]) row.z = yAxisItemData[yFormatIndex];
				if (yHiddenIndex > -1 && yAxisItemData[yHiddenIndex] == '1') row.hidden = true;
				if (yCollapsedIndex > -1 && yAxisItemData[yCollapsedIndex] == '1') row.collapsed = true;
				if (yDrillIndex > -1 && yAxisItemData[yDrillIndex] == '0') row.clickable = false;
				if (yViewOptionsIndex > -1 && yAxisItemData[yViewOptionsIndex]) row.props.viewOptions = yAxisItemData[yViewOptionsIndex];
			}

			// numeric column values
			for (j = 0; j < valueColumns.length; j++) {
				valueColumn = valueColumns[j];
				indicator = valueColumn._indicator ?? indicator;
				xKey = valueColumn._index[0];
				// statement rows with no data row at all have no dataIndex record
				dataRowIndex = this.result.dataIndex.get(yKey)?.[xKey];
				if (dataRowIndex != null) {
					dataRow = this.result.data.data[dataRowIndex];
					value = dataRow[indicator.dataColumnIndex];
					unit = indicator.unitDataColumnIndex != null ? dataRow[indicator.unitDataColumnIndex] : undefined;
					if (value == null || value === '') {
						value = nullsToZeros ? 0 : null;
					} else if (indicator.displayUnit > 1) {
						if (!row.z || !row.z.endsWith('%')) {
							value /= indicator.displayUnit;
						}
					}

					row.data[valueColumn.data as number] = unit ? {
						v: value,
						z: indicator.format + '" ' + unit + '"'
					} : value;
				} else {
					row.data[valueColumn.data as number] = nullsToZeros ? 0 : null;
				}
			}

			// without text columns (no dimension on y axis) data[textColumnOffset] is the first value, not a label
			if (textColumns.length > textColumnOffset && (row.data[textColumnOffset] == null || row.data[textColumnOffset] == '')) {
				row = formatBlankRow(row, textColumnOffset);
			}

			if (hasIndicators && indicator?.renderer) {
				this.initRowRenderer(row, rows.length, indicator);
			}

			rows.push(row);
			prevItemIndexes = itemIndexes;
		}

		this._rows = parentBasedTree ? rows: filterLowerLevels(normalizeLevels(rows));

		// omit redundant rows
		// if (hasIndicators) {
		// 	const rowsToOmit = new Set<number>();
		// 	for (let r = 0; r < rows.length; r++) {
		// 		if (!rows[r]._i) continue;

		// 		// find next non indicator row
		// 		let r2 = r + 1;
		// 		while (rows[r2]?._i) {
		// 			++r2;
		// 		}
		// 		const nextNonIndicatorRow = rows[r2];
		// 		if (!nextNonIndicatorRow) break;

		// 		if (r < rows.length - 1 && nextNonIndicatorRow.l == rows[r].l) {
		// 			rowsToOmit.add(r);
		// 			let r3 = r - 1;
		// 			while (r3 >= 0 && rows[r3].l >= nextNonIndicatorRow.l) {
		// 				rowsToOmit.add(r3--);
		// 			}
		// 		};
		// 	}

		// 	this._rows = [];

		// 	for (let r = 0; r < rows.length; r++) {
		// 		if (!rowsToOmit.has(r)) {
		// 			if (rows[r]._g) this._groupRows.add(this._rows.length);
		// 			this._rows.push(rows[r]);
		// 		}
		// 	}
		// }

		return this._rows;
	}

	initRowRenderer(row: RowSource, index: number, indicator: IndicatorResult) {
		row.renderer = this.getCellRenderer(indicator.renderer as IndicatorRenderer, 'row');
		if (row.renderer) {
			row.props.renderer = indicator.renderer;
			this._customRendererRows.add(index);
			for (let j = 0; j < this._columns.length; j++) {
				if (!utils.isObject(row.data[j])) {
					row.data[j] = {v: row.data[j]};
				}
				if (j < this._textColumns.length) {
					row.data[j].renderer = renderers.TextRenderer;
				} else if (j < this._columns.length - 1) {
					row.data[j].s = {right: {style: 'none'}};
				}
			}

			if (indicator.renderer == 'waterfall') {
				row.h = WATERFALL_ROW_HEIGHT;
				for (let j = 0; j < this._columns.length; j++) {
					const column = this._columns[j];
					if (j < this._textColumns.length) {
						column.w = DEFAULT_COLUMN_WIDTH;
					} else {
						column.w = WATERFALL_HEADER_WIDTH;
						let firstHeader = column.header as ColumnHeaderModel[];
						if (!firstHeader) firstHeader = [];
						if (firstHeader.length === 0) firstHeader.push({});
						firstHeader[0].h = WATERFALL_HEADER_HEIGHT;
						firstHeader[0].style = WATERFALL_HEADER_STYLE;
					}
				}
			}
		}
	}


	async getCells(filters?: RequestFilters): Promise<CellSource[]> {
		if (this.result.isEmpty) return [];

		const textColumnOffset = this.textColumnOffset;
		// const entireRowColspan = this.getColumns().length - textColumnOffset;
		const cells: CellSource[] = [];

		this._rows.forEach((row, r) => {
			let styles = !this.suppressLegacyStyles && this._rowStyles.has(r)
				? convertLegacyCellStyles(r, this._rows[r], this._textColumns, this._valueColumns, this._rowStyles.get(r))
				: null;

			if (row.props.indent) { // fin row indent settings
				if (!styles) styles = {};
				if (!styles[textColumnOffset]) styles[textColumnOffset] = {}
				if (!styles[textColumnOffset].alignment) styles[textColumnOffset].alignment = {};
				styles[textColumnOffset].alignment.indent = row.props.indent as number;
			}

			if (styles) {
				for (const c in styles) {
					cells.push({r: r, c: parseInt(c), s: styles[c]});
				}
			}
		});

		return cells;
	}


	async getRules(): Promise<ConditionalFormatSource> {
		const rules: ConditionalFormatSource = [];
		const rows = await this.getRows();
		const ranges: string[] = [];
		const ruleMap: Record<string, string[]> = {};
		const offset = this.getTextColumns().length;
		this.getValueColumns().forEach((col, c) => {
			const indicator = col.props?.indicator as IndicatorResult;
			if (!indicator) return;
			if (indicator.renderer == 'bar') {
				if (!ruleMap['bar']) ruleMap['bar'] = [];
				ruleMap['bar'].push((new CellRange(offset + c, 0, offset + c, rows.length - 1)).A1);
			}
		});

		for (const key in ruleMap) {
			rules.push({
				type: key as ConditionalFormatItemSource['type'],
				ranges: ruleMap[key].join(';'),
				color: 'C6D9F1',
				cmax: {t: 'percent', v: 50},
			});
		}

		return rules;
	}

	/** comments of the report mapped to grid positions by the row/column props.key */
	getComments(): CommentSource[] {
		const comments = this.result.comments;
		if (!comments?.length) return null;

		// the caches are populated - the grid asks for comments after columns/rows
		const rowIndex = new Map<string, number>();
		(this._rows ?? []).forEach((row, r) => {
			const key = (row.props as AxisItemProps)?.key;
			if (key != null) rowIndex.set(key, r);
		});
		const columnIndex = new Map<string, number>();
		(this._columns ?? []).forEach((column, c) => {
			const key = (column.props as AxisItemProps)?.key;
			if (key != null) columnIndex.set(key, c);
		});

		const userId = rp.userProvider.id;
		const isAdmin = rp.userProvider.hasRight('admin');

		return comments.map(comment => {
			const r = rowIndex.get(comment.rowKey);
			const c = columnIndex.get(comment.columnKey);
			// keys not present in the current layout (collapsed levels, changed axes) are skipped
			if (r == null || c == null) return null;
			return {
				r, c,
				text: comment.text,
				date: comment.dateCreated,
				author: {id: comment.whoCreated, name: comment.whoCreated},
				readOnly: comment.whoCreated != userId && !isAdmin,
				props: {idz: comment.idz},
			} as CommentSource;
		}).filter(Boolean);
	}

	getOptions(): Options {
		return {
			hideOuterBorder: true,
			rows: 100,
			columns: 20,
			autofill: false,
			rowTree: {enabled: false},
			columnTree: { enabled: false },
			columnHeaderTreeButtons: true,
			unhideRowAfterExpand: true,
			emptyPlaceholder: true,
			scrolling: { scrollbarVisibilityTreshold: 0 },
			rowNumbers: false,
			colLetters: false,
			levelButtonCell: this.result.yAxis.length && this.result.xAxis.length ? {r: -this.headerRowCount, c: 0} : null,
			freezeLeft: this.valueColumnOffset,
			sorting: {
				defaultSettings: (this.contextData.view_options as TableViewOptions)?.sorting || []
			},
			// author of newly added comments
			user: {
				id: rp.userProvider.id,
				name: rp.userProvider.user?.login_name ?? rp.userProvider.id
			}
		};
	}

	get isEmpty() {
		return this.result.isEmpty;
	}

}
